import { Core } from "@faire/design-tokens";
import { FaireDate } from "@faire/web/common/FaireDate";
import {
  FaireDateRange,
  PresetDateFormat,
} from "@faire/web/common/FaireDateRange";
import {
  StrictLocalizeFunction,
  useStrictLocalization,
} from "@faire/web/common/localization";
import { MediumFormatting } from "@faire/web/common/localization/formatting";
import { Information } from "@faire/web/slate/icons/foundation/Information";
import { Row } from "@faire/web/slate/Layout";
import { Spacer } from "@faire/web/slate/spacing";
import { Tooltip } from "@faire/web/slate/Tooltip";
import { Position } from "@faire/web/slate/Tooltip/common";
import { TooltipTypography, Typography } from "@faire/web/slate/Typography";
import { useViewport } from "@faire/web/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import addDays from "date-fns/addDays";
import * as React from "react";

import { useSettingOwFImproveTermsTouchpointsPDPShipping } from "@faire/retailer/settings/useSettingOwFImproveTermsTouchpointsPDPShipping";
import { IBrandShipping } from "@faire/retailer/stores/domain/CartStore/consts";

import { isShippingEstimateInfoValid } from "./utils";

interface IProps {
  brand: IBrandShipping;
  showMobileTooltip?: boolean;
  preorderDateRange?: FaireDateRange;
  inModal?: boolean;
  className?: string;
  showTooltipIcon?: boolean;
}

export const ShippingEstimate: React.FC<IProps> = ({
  brand,
  showMobileTooltip,
  preorderDateRange,
  inModal,
  className,
  showTooltipIcon,
}) => {
  const { strictLocalize } = useStrictLocalization();
  const { isMobile, isDesktopAndAbove } = useViewport();
  const settingOwFImproveTermsTouchpointsPDPShipping =
    useSettingOwFImproveTermsTouchpointsPDPShipping();

  if (isShippingEstimateInfoValid(brand)) {
    let lowEstimate: Date;
    let highEstimate: Date;
    if (preorderDateRange) {
      lowEstimate = addDays(
        preorderDateRange.startDate.normalizedUtcTimestamp,
        brand.shipping_time_lower_bound_days ?? 0
      );
      highEstimate = addDays(
        preorderDateRange.endDate.normalizedUtcTimestamp,
        brand.shipping_time_upper_bound_days ?? 0
      );
    } else {
      lowEstimate = addDays(
        Date.now(),
        (brand.lower_bound_lead_time_days ?? 0) +
          (brand.shipping_time_lower_bound_days ?? 0)
      );
      highEstimate = addDays(
        Date.now(),
        (brand.upper_bound_lead_time_days ?? 0) +
          (brand.shipping_time_upper_bound_days ?? 0)
      );
    }

    const dateRange = new FaireDateRange(
      FaireDate.fromLocalDateObject(lowEstimate),
      FaireDate.fromLocalDateObject(highEstimate)
    );

    const formattedDate =
      dateRange.format({
        presetDateFormat: PresetDateFormat.ABBREVIATED_MMM_D,
      }) ?? "";

    const tooltipPosition = isMobile || !inModal ? "top" : "bottom";

    const renderDeliveryText = () => {
      return showTooltipIcon ? (
        <StrictLocalMsg
          defaultMessage="Get it by <MediumFormatting>{deliveryDate}</MediumFormatting> if you order today."
          description={{
            text: "the estimated date range for the delivery date of an order",
            img: "https://cdn.faire.com/fastly/e95789b834a5513dba8c4a01f1a38a043012b080e2916fed4fb7c3efd0cf1887.png",
          }}
          values={{
            MediumFormatting: MediumFormatting,
            deliveryDate: formattedDate,
          }}
        />
      ) : (
        <StrictLocalMsg
          defaultMessage="Estimated delivery: <DeliveryDateFormatting>{deliveryDate}</DeliveryDateFormatting>"
          description={{
            text: "the estimated date range for the delivery date of an order",
          }}
          values={{
            TextFormatting: TextFormatting,
            DeliveryDateFormatting: (chunk: JSX.Element) =>
              DeliveryDateFormatting(chunk, tooltipPosition, strictLocalize),
            deliveryDate: formattedDate,
          }}
        />
      );
    };

    const getShippingTooltip = () => {
      return (
        <Tooltip
          position={tooltipPosition}
          icon={<Information color={Core.icon.subdued} fontSize="16px" />}
          pointerOffset={isMobile ? 0 : 100}
          contents={[
            {
              body: strictLocalize({
                defaultMessage:
                  "This delivery estimate is based on the time it takes the brand to ship your order, and the time it takes the carrier to deliver it.",
                description: {
                  text: "explanation of how the estimated shipping date of an order is calculated.",
                  img: "https://cdn.faire.com/fastly/76e8d3031f0905fb9e133c00439f247fe758abc34cdda93f4b68a885d15fbff9.png",
                },
              }),
            },
          ]}
        />
      );
    };

    if (settingOwFImproveTermsTouchpointsPDPShipping) {
      return (
        <Row style={{ width: "100%" }} className={className} gap={4}>
          <StrictLocalMsg
            defaultMessage="<TextFormatting>Get it by</TextFormatting> <DeliveryDateFormatting>{deliveryDate}</DeliveryDateFormatting>"
            description={{
              text: "Telling retailer when they may get a product by if they place an order",
              img: "https://cdn.faire.com/fastly/2d9c607a531a3565072d5b59213d3c7a267f09fc22115eae6202487932c10256.png",
            }}
            values={{
              TextFormatting: TextFormatting,
              DeliveryDateFormatting: (chunk: JSX.Element) =>
                DeliveryDateFormatting(
                  chunk,
                  tooltipPosition,
                  strictLocalize,
                  isMobile ? -40 : 0
                ),
              deliveryDate: formattedDate,
            }}
          />
        </Row>
      );
    }

    return (
      <Row style={{ width: "100%" }} className={className}>
        <Typography>{renderDeliveryText()}</Typography>
        {(isDesktopAndAbove && showTooltipIcon) || showMobileTooltip ? (
          <>
            <Spacer size={4} />
            {getShippingTooltip()}
          </>
        ) : null}
      </Row>
    );
  } else {
    return null;
  }
};

const TextFormatting = (chunk: JSX.Element) => <Typography>{chunk}</Typography>;
const DeliveryDateFormatting = (
  chunk: JSX.Element,
  tooltipPosition: Position,
  strictLocalize: StrictLocalizeFunction,
  pointerOffset?: number
) => (
  <Tooltip
    position={tooltipPosition}
    pointerOffset={pointerOffset}
    targetNode={<TooltipTypography>{chunk}</TooltipTypography>}
    contents={[
      {
        body: strictLocalize({
          defaultMessage:
            "This delivery estimate is based on the time it will take for the brand to ship out your order and for the carrier to deliver it.",
          description: {
            text: "explanation shown on hover of how the estimated shipping date of an order is calculated",
            img: "https://cdn.faire.com/fastly/790b75aefbffe81138a200807cc250beb47815b3797bcf5b877bbd32eb528eb8.png",
          },
        }),
      },
    ]}
  />
);
